import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { api } from "@/services/api/client";
import type {
  OrganizationSettings as PermissionsOrganizationSettings,
  PasswordPolicy as PermissionsPasswordPolicy,
} from "@/types/permissions";
import { isRecord, storage } from "@/utils";
import { logger } from "@/utils/logger";
import type { OrganizationRole } from "@/utils/organizationPermissions";

/**
 * Phase 1: Organization Store
 * Manages organization context, membership, and switching between user/org contexts
 *
 * Platform Admin Mode: When isPlatformAdmin is true, the user can see all
 * organizations in the system and switch between org contexts.
 */

export interface Organization {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  ownerId: string;
  securityPolicy: OrganizationApiSecurityPolicy;
  compliance: OrganizationApiComplianceSettings;
  billingPlan: {
    type: "free" | "basic" | "standard" | "pro" | "enterprise";
    status: "active" | "trialing" | "past_due" | "canceled" | "suspended";
    provider?: "stripe" | "internal";
    currentPeriodEnd?: string;
    subscriptionId?: string;
    stripeCustomerId?: string;
  };
  resourceLimits: {
    maxProjects: number;
    maxComponents: number;
    maxStorage: number; // MB
    maxApiCalls: number;
    maxTeams: number;
    maxDepartments: number;
  };
  settings: OrganizationApiSettings;
  membership?: {
    role: OrganizationRole;
    joinedAt: string;
    isPrimary?: boolean;
    status?: string;
  };
  isPlatformAdmin?: boolean; // True if this org has platform admin privileges
  createdAt: string;
  updatedAt: string;
}

export type OrganizationSettings = PermissionsOrganizationSettings;

export type PasswordPolicy = PermissionsPasswordPolicy;

export interface OrganizationApiPasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  expirationDays: number;
  preventReuse: number;
}

export interface OrganizationApiSecurityPolicy {
  passwordPolicy: OrganizationApiPasswordPolicy;
  mfaRequired: boolean;
  mfaTypes: Array<"totp" | "sms" | "email" | "webauthn">;
  sessionTimeout: number;
  ipWhitelist: string[];
  ipBlacklist: string[];
  allowedDomains: string[];
  ssoEnabled: boolean;
  ssoProvider?: string;
  dataRetentionDays: number;
  encryptionRequired: boolean;
}

export interface OrganizationApiComplianceSettings {
  gdprEnabled: boolean;
  hipaaEnabled: boolean;
  soc2Enabled: boolean;
  isoEnabled: boolean;
  dataResidency?: string;
  auditLogRetention: number;
  consentRequired: boolean;
  rightToDelete: boolean;
}

export interface OrganizationApiSettings {
  defaultRole:
    | "super_admin"
    | "owner"
    | "admin"
    | "member"
    | "organization_admin"
    | "team_lead"
    | "designer"
    | "viewer"
    | "guest";
  allowGuestAccess: boolean;
  requireApproval: boolean;
  allowPublicProjects: boolean;
  brandingEnabled: boolean;
  customDomain?: string;
  storage?: {
    defaultProvider?:
      | "cloudflare-r2"
      | "aws-s3"
      | "minio"
      | "seaweedfs"
      | "local-fs"
      | "onedrive"
      | "gcs"
      | "azure-blob";
    allowUserSelection?: boolean;
    enabledProviders?: Array<
      | "cloudflare-r2"
      | "aws-s3"
      | "minio"
      | "seaweedfs"
      | "local-fs"
      | "onedrive"
      | "gcs"
      | "azure-blob"
    >;
    enforceEncryption?: boolean;
    autoBackup?: boolean;
    retentionPolicyDays?: number;
    compressionEnabled?: boolean;
    quotaPerUser?: number;
    alertThresholds?: {
      warning?: number;
      critical?: number;
      emergency?: number;
    };
  };
  notifications?: {
    sendDeclineEmails?: boolean;
    sendAcceptanceEmails?: boolean;
    sendExpiryReminders?: boolean;
  };
}

export interface OrganizationMember {
  userId: string;
  membershipId?: string; // For resend/cancel actions
  username: string;
  email: string;
  name: string;
  avatar?: string;
  role: OrganizationRole;
  status: "active" | "pending" | "invited" | "suspended" | "removed";
  joinedAt: string;
  // Invitation-specific fields for pending members
  invitedAt?: string;
  expiresAt?: string;
  resendCount?: number;
  maxResends?: number;
}

export interface OwnerContext {
  type: "user" | "organization";
  id: string;
  username?: string; // For user context
  orgSlug?: string; // For org context
  displayName: string;
}

interface OrganizationState {
  // State
  organizations: Organization[];
  allOrganizations: Organization[]; // All orgs in system (for platform admins)
  currentContext: OwnerContext | null;
  pendingRestoredOrganizationContext: OwnerContext | null;
  isLoading: boolean;
  error: string | null;
  initialized: boolean;
  isPlatformAdmin: boolean; // True when platform-admin mode is active
  platformAdminEntitlement: boolean; // True if current user is entitled to platform admin access

  // Actions
  fetchOrganizations: () => Promise<void>;
  fetchAllOrganizations: () => Promise<void>; // For platform admins
  createOrganization: (data: CreateOrganizationData) => Promise<Organization>;
  updateOrganization: (
    slug: string,
    data: UpdateOrganizationData,
  ) => Promise<Organization>;
  deleteOrganization: (slug: string) => Promise<void>;
  getOrganization: (slug: string) => Promise<Organization>;

  // Member management
  getMembers: (slug: string) => Promise<OrganizationMember[]>;
  inviteMember: (
    slug: string,
    data: InviteMemberData,
  ) => Promise<{ emailSent: boolean; email: string }>;
  updateMemberRole: (
    slug: string,
    memberId: string,
    role: string,
  ) => Promise<void>;
  removeMember: (slug: string, memberId: string) => Promise<void>;
  resendInvitation: (
    slug: string,
    membershipId: string,
  ) => Promise<{
    resendCount: number;
    maxResends: number;
    emailSent: boolean;
    email: string;
  }>;
  cancelInvitation: (slug: string, membershipId: string) => Promise<void>;

  // Context switching
  switchToUserContext: (username: string) => void;
  switchToOrgContext: (orgSlug: string) => void;
  getCurrentContext: () => OwnerContext | null;
  clearError: () => void;
  initialize: () => void;
  updatePlatformAdminStatus: (platformAdminEntitlement?: boolean) => void;
  validateCurrentContext: (options?: {
    allowPendingPlatformContext?: boolean;
  }) => void;
  clearCurrentContext: () => void;
}

export interface CreateOrganizationData {
  name: string;
  slug: string;
  description?: string;
}

export interface UpdateOrganizationData {
  name?: string;
  description?: string;
  settings?: Record<string, unknown>;
  securityPolicy?: OrganizationApiSecurityPolicy;
  compliance?: OrganizationApiComplianceSettings;
}

export interface InviteMemberData {
  email: string;
  role: "admin" | "member" | "viewer";
}

const STORAGE_KEY = "terramatrix:org-context";

function removeStoredOrganizationContext(): void {
  storage.remove(STORAGE_KEY);
  if (typeof window !== "undefined") {
    window.sessionStorage?.removeItem(STORAGE_KEY);
  }
}

function persistOrganizationContext(context: OwnerContext): void {
  if (typeof window !== "undefined") {
    window.sessionStorage?.removeItem(STORAGE_KEY);
  }
  storage.set(STORAGE_KEY, context);
}

function isOwnerContext(value: unknown): value is OwnerContext {
  if (!isRecord(value)) {
    return false;
  }

  const { type, id, username, orgSlug, displayName } = value;
  return (
    (type === "user" || type === "organization") &&
    typeof id === "string" &&
    typeof displayName === "string" &&
    (username === undefined || typeof username === "string") &&
    (orgSlug === undefined || typeof orgSlug === "string")
  );
}

const findOrganizationForContext = (
  orgSlug: string,
  organizations: Organization[],
  allOrganizations: Organization[],
  platformAdminEntitlement: boolean,
): Organization | undefined =>
  organizations.find((org) => org.slug === orgSlug) ??
  (platformAdminEntitlement
    ? allOrganizations.find((org) => org.slug === orgSlug)
    : undefined);

const contextFromOrganization = (org: Organization): OwnerContext => ({
  type: "organization",
  id: org._id,
  orgSlug: org.slug,
  displayName: org.name,
});

const isContextAuthorized = (
  context: OwnerContext,
  organizations: Organization[],
  allOrganizations: Organization[],
  platformAdminEntitlement: boolean,
): boolean => {
  if (context.type === "user") {
    return true;
  }

  if (!context.orgSlug) {
    return false;
  }

  const memberOrg = organizations.some((org) => org.slug === context.orgSlug);
  const platformVisibleOrg =
    platformAdminEntitlement &&
    allOrganizations.some((org) => org.slug === context.orgSlug);

  return memberOrg || platformVisibleOrg;
};

function reconcileAuthPlatformAdminEntitlement(isPlatformAdmin: boolean): void {
  void import("@/stores/auth/auth-store")
    .then(({ useAuthStore }) => {
      if (!useAuthStore?.getState) {
        return;
      }
      useAuthStore
        .getState()
        .reconcilePlatformAdminEntitlement(isPlatformAdmin);
    })
    .catch((error) => {
      logger.warn("Failed to reconcile auth platform admin entitlement", {
        error,
      });
    });
}

export const useOrganizationStore = create<OrganizationState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      // Initial state
      organizations: [],
      allOrganizations: [],
      currentContext: null,
      pendingRestoredOrganizationContext: null,
      isLoading: false,
      error: null,
      initialized: false,
      isPlatformAdmin: false,
      platformAdminEntitlement: false,

      // Platform-admin entitlement is mirrored from auth-store authoritative
      // session state, which is reconciled against /auth/me when live server
      // authority is available.
      updatePlatformAdminStatus: (
        platformAdminEntitlement = get().platformAdminEntitlement,
      ) => {
        set((state) => {
          state.platformAdminEntitlement = platformAdminEntitlement;
          if (!platformAdminEntitlement) {
            state.allOrganizations = [];
            if (
              state.initialized &&
              state.currentContext?.type === "organization" &&
              !state.organizations.some(
                (org) => org.slug === state.currentContext?.orgSlug,
              )
            ) {
              state.currentContext = null;
              removeStoredOrganizationContext();
            }
          }
          state.isPlatformAdmin =
            state.currentContext?.type === "user"
              ? false
              : state.platformAdminEntitlement;
        });
        if (!platformAdminEntitlement) {
          reconcileAuthPlatformAdminEntitlement(false);
        }
        logger.debug("Platform admin status updated", {
          isPlatformAdmin:
            get().currentContext?.type === "user"
              ? false
              : get().platformAdminEntitlement,
          platformAdminEntitlement: get().platformAdminEntitlement,
        });
      },

      // Fetch user's organizations
      fetchOrganizations: async () => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await api.get<Organization[]>("/organizations");

          const orgs = response.data || [];
          const platformAdminEntitlement = get().platformAdminEntitlement;
          set((state) => {
            state.organizations = orgs;
            state.platformAdminEntitlement = platformAdminEntitlement;
            if (!platformAdminEntitlement) {
              state.allOrganizations = [];
            }
            state.isPlatformAdmin =
              state.currentContext?.type === "user"
                ? false
                : platformAdminEntitlement;
            state.isLoading = false;
          });
          get().validateCurrentContext({ allowPendingPlatformContext: true });
        } catch (error) {
          set((state) => {
            state.platformAdminEntitlement = false;
            state.isPlatformAdmin = false;
            state.allOrganizations = [];
            state.pendingRestoredOrganizationContext = null;
            state.initialized = true;
            if (state.currentContext?.type === "organization") {
              state.currentContext = null;
              removeStoredOrganizationContext();
            }
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to fetch organizations";
            state.isLoading = false;
          });
          reconcileAuthPlatformAdminEntitlement(false);
          throw error;
        }
      },

      // Fetch ALL organizations (for platform admins only)
      fetchAllOrganizations: async () => {
        const { platformAdminEntitlement } = get();
        if (!platformAdminEntitlement) {
          console.warn(
            "fetchAllOrganizations called but user is not platform admin",
          );
          return;
        }

        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          // This endpoint returns all organizations for platform admins
          const response = await api.get<Organization[]>("/organizations/all");

          set((state) => {
            state.allOrganizations = response.data || [];
            state.isLoading = false;
          });
          get().validateCurrentContext();
          logger.debug("Fetched all organizations", {
            count: response.data?.length || 0,
          });
        } catch (error) {
          set((state) => {
            // Conservatively clear platform-admin entitlement whenever all-org
            // visibility cannot be verified from live backend state.
            state.platformAdminEntitlement = false;
            state.isPlatformAdmin = false;
            state.allOrganizations = [];
            state.pendingRestoredOrganizationContext = null;
            state.initialized = true;
            if (
              state.currentContext?.type === "organization" &&
              !state.organizations.some(
                (org) => org.slug === state.currentContext?.orgSlug,
              )
            ) {
              state.currentContext = null;
              removeStoredOrganizationContext();
            }
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to fetch all organizations";
            state.isLoading = false;
          });
          reconcileAuthPlatformAdminEntitlement(false);
          throw error;
        }
      },

      // Create new organization
      createOrganization: async (data: CreateOrganizationData) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await api.post<Organization, CreateOrganizationData>(
            "/organizations",
            data,
          );

          const newOrg = response.data;
          if (!newOrg) {
            throw new Error("Failed to create organization: no data returned");
          }

          set((state) => {
            state.organizations.push(newOrg);
            state.isLoading = false;
          });

          return newOrg;
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to create organization";
            state.isLoading = false;
          });
          throw error;
        }
      },

      // Update organization
      updateOrganization: async (
        slug: string,
        data: UpdateOrganizationData,
      ) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await api.patch<
            Organization,
            UpdateOrganizationData
          >(`/organizations/${slug}`, data);

          const updatedOrg = response.data;
          if (!updatedOrg) {
            throw new Error("Failed to update organization: no data returned");
          }

          set((state) => {
            const index = state.organizations.findIndex(
              (org) => org.slug === slug,
            );
            if (index !== -1) {
              state.organizations[index] = updatedOrg;
            }
            state.isLoading = false;
          });

          return updatedOrg;
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to update organization";
            state.isLoading = false;
          });
          throw error;
        }
      },

      // Delete organization
      deleteOrganization: async (slug: string) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          await api.delete(`/organizations/${slug}`);

          set((state) => {
            state.organizations = state.organizations.filter(
              (org) => org.slug !== slug,
            );

            // If deleting current context org, switch to user context
            if (
              state.currentContext?.type === "organization" &&
              state.currentContext.orgSlug === slug
            ) {
              state.currentContext = null;
              removeStoredOrganizationContext();
            }
            if (state.pendingRestoredOrganizationContext?.orgSlug === slug) {
              state.pendingRestoredOrganizationContext = null;
            }

            state.isLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to delete organization";
            state.isLoading = false;
          });
          throw error;
        }
      },

      // Get specific organization
      getOrganization: async (slug: string) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await api.get<Organization>(
            `/organizations/${slug}`,
          );

          const org = response.data;
          if (!org) {
            throw new Error("Failed to fetch organization: no data returned");
          }

          set((state) => {
            const index = state.organizations.findIndex((o) => o.slug === slug);
            if (index === -1) {
              state.organizations.push(org);
            } else {
              state.organizations[index] = org;
            }
            state.isLoading = false;
          });

          return org;
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to fetch organization";
            state.isLoading = false;
          });
          throw error;
        }
      },

      // Get organization members
      getMembers: async (slug: string) => {
        try {
          const response = await api.get<OrganizationMember[]>(
            `/organizations/${slug}/members`,
          );

          return response.data || [];
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to fetch members";
          });
          throw error;
        }
      },

      // Invite member to organization
      inviteMember: async (slug: string, data: InviteMemberData) => {
        try {
          const response = await api.post<
            { emailSent: boolean; email: string },
            InviteMemberData
          >(`/organizations/${slug}/members`, data);
          logger.debug("[ORG-STORE] Invite response received", {
            emailSent: response.data?.emailSent ?? true,
          });
          return {
            emailSent: response.data?.emailSent ?? true,
            email: response.data?.email || data.email,
          };
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to invite member";
          });
          throw error;
        }
      },

      // Update member role
      updateMemberRole: async (
        slug: string,
        memberId: string,
        role: string,
      ) => {
        try {
          await api.patch<void, { role: string }>(
            `/organizations/${slug}/members/${memberId}`,
            { role },
          );
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to update member role";
          });
          throw error;
        }
      },

      // Remove member from organization
      removeMember: async (slug: string, memberId: string) => {
        try {
          await api.delete(`/organizations/${slug}/members/${memberId}`);
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to remove member";
          });
          throw error;
        }
      },

      // Resend invitation to pending member
      resendInvitation: async (slug: string, membershipId: string) => {
        try {
          const response = await api.post<{
            resendCount: number;
            maxResends: number;
            emailSent: boolean;
            email: string;
          }>(`/organizations/${slug}/invitations/${membershipId}/resend`, {});

          return {
            resendCount: response.data?.resendCount || 1,
            maxResends: response.data?.maxResends || 3,
            emailSent: response.data?.emailSent ?? true,
            email: response.data?.email || "",
          };
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to resend invitation";
          });
          throw error;
        }
      },

      // Cancel a pending invitation
      cancelInvitation: async (slug: string, membershipId: string) => {
        try {
          await api.delete(
            `/organizations/${slug}/invitations/${membershipId}`,
          );
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to cancel invitation";
          });
          throw error;
        }
      },

      // Switch to user context
      switchToUserContext: (username: string) => {
        const context: OwnerContext = {
          type: "user",
          id: username,
          username,
          displayName: username,
        };

        set((state) => {
          state.currentContext = context;
          state.pendingRestoredOrganizationContext = null;
          state.isPlatformAdmin = false; // Personal context is never platform admin
          state.initialized = true;
        });
        logger.debug("Platform admin status changed for user context", {
          isPlatformAdmin: false,
        });

        // Persist to storage
        persistOrganizationContext(context);
      },

      // Switch to organization context
      switchToOrgContext: (orgSlug: string) => {
        const { organizations, allOrganizations, platformAdminEntitlement } =
          get();
        const org = findOrganizationForContext(
          orgSlug,
          organizations,
          allOrganizations,
          platformAdminEntitlement,
        );

        if (!org) {
          set((state) => {
            state.error = "Organization context unavailable";
            state.isPlatformAdmin = false;
          });
          logger.warn("Organization context switch rejected", { orgSlug });
          return;
        }

        const context = contextFromOrganization(org);

        set((state) => {
          state.currentContext = context;
          state.pendingRestoredOrganizationContext = null;
          state.isPlatformAdmin = platformAdminEntitlement;
          state.initialized = true;
        });
        logger.debug("Platform admin status changed for org context", {
          isPlatformAdmin: platformAdminEntitlement,
          orgSlug,
        });

        // Persist to storage
        persistOrganizationContext(context);
      },

      // Get current context
      getCurrentContext: () => {
        return get().currentContext;
      },

      // Clear error
      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },

      // Initialize store
      initialize: () => {
        if (get().initialized || get().pendingRestoredOrganizationContext) {
          return;
        }

        // Restore context from storage
        const savedContext = storage.get<OwnerContext | null>(
          STORAGE_KEY,
          null,
          isOwnerContext,
        );
        if (savedContext) {
          if (savedContext.type === "organization") {
            set((state) => {
              state.currentContext = null;
              state.pendingRestoredOrganizationContext = savedContext;
              state.initialized = false;
            });
            removeStoredOrganizationContext();
          } else {
            set((state) => {
              state.currentContext = savedContext;
              state.pendingRestoredOrganizationContext = null;
              state.initialized = true;
            });
          }
        } else {
          set((state) => {
            state.initialized = true;
          });
        }

        // Re-apply the current auth-store-derived entitlement after context restoration.
        get().updatePlatformAdminStatus();
      },

      validateCurrentContext: (options = {}) => {
        const {
          currentContext,
          pendingRestoredOrganizationContext,
          organizations,
          allOrganizations,
          platformAdminEntitlement,
        } = get();
        const contextToValidate =
          pendingRestoredOrganizationContext ?? currentContext;
        const validatingPendingRestore =
          pendingRestoredOrganizationContext !== null;

        if (!contextToValidate) {
          set((state) => {
            state.initialized = true;
          });
          removeStoredOrganizationContext();
          return;
        }

        if (
          contextToValidate.type === "organization" &&
          options.allowPendingPlatformContext &&
          platformAdminEntitlement &&
          allOrganizations.length === 0 &&
          !organizations.some((org) => org.slug === contextToValidate.orgSlug)
        ) {
          removeStoredOrganizationContext();
          return;
        }

        if (
          isContextAuthorized(
            contextToValidate,
            organizations,
            allOrganizations,
            platformAdminEntitlement,
          )
        ) {
          const authorizedOrg =
            contextToValidate.type === "organization" &&
            contextToValidate.orgSlug
              ? findOrganizationForContext(
                  contextToValidate.orgSlug,
                  organizations,
                  allOrganizations,
                  platformAdminEntitlement,
                )
              : undefined;
          const context =
            authorizedOrg === undefined
              ? contextToValidate
              : contextFromOrganization(authorizedOrg);

          set((state) => {
            if (validatingPendingRestore) {
              state.pendingRestoredOrganizationContext = null;
            }
            state.currentContext = context;
            state.isPlatformAdmin =
              context.type === "user" ? false : state.platformAdminEntitlement;
            state.initialized = true;
          });
          persistOrganizationContext(context);
          return;
        }

        set((state) => {
          if (validatingPendingRestore) {
            state.pendingRestoredOrganizationContext = null;
          }
          state.currentContext = null;
          state.isPlatformAdmin = false;
          state.initialized = true;
        });
        removeStoredOrganizationContext();
      },

      clearCurrentContext: () => {
        set((state) => {
          state.currentContext = null;
          state.pendingRestoredOrganizationContext = null;
          state.isPlatformAdmin = false;
          state.initialized = true;
        });
        removeStoredOrganizationContext();
      },
    })),
  ),
);

const bootstrapOrganizationAccess = async (): Promise<void> => {
  try {
    const store = useOrganizationStore.getState();
    await store.fetchOrganizations();

    const { platformAdminEntitlement, fetchAllOrganizations } =
      useOrganizationStore.getState();
    if (platformAdminEntitlement) {
      await fetchAllOrganizations();
    }
  } catch (error) {
    logger.error("Failed to bootstrap organization access", error);
  }
};

// Module-level initialization — restore currentContext from persisted storage on routes
// (same pattern as auth-store.ts:587)
useOrganizationStore.getState().initialize();

// Subscribe to auth store to reset organization store on logout
if (typeof window !== "undefined") {
  import("@/stores/auth/auth-store").then(({ useAuthStore }) => {
    if (!useAuthStore?.subscribe) {
      return;
    }

    const syncPlatformAdminEntitlement = (): void => {
      useOrganizationStore
        .getState()
        .updatePlatformAdminStatus(
          useAuthStore.getState().platformAdminEntitlement === true,
        );
    };

    useAuthStore.subscribe(
      (state) => state.platformAdminEntitlement === true,
      (platformAdminEntitlement) => {
        useOrganizationStore
          .getState()
          .updatePlatformAdminStatus(platformAdminEntitlement);
      },
    );

    useAuthStore.subscribe(
      (state) => state.isAuthenticated,
      (isAuthenticated, previousIsAuthenticated) => {
        // If user logged out, clear organization state
        if (previousIsAuthenticated && !isAuthenticated) {
          useOrganizationStore.setState({
            organizations: [],
            allOrganizations: [],
            currentContext: null,
            pendingRestoredOrganizationContext: null,
            error: null,
            isPlatformAdmin: false,
            platformAdminEntitlement: false,
            initialized: false,
          });
          removeStoredOrganizationContext();
        }
        // If user logged in, update platform admin status and fetch organizations
        else if (!previousIsAuthenticated && isAuthenticated) {
          syncPlatformAdminEntitlement();
          void bootstrapOrganizationAccess();
        }
      },
    );

    // Handle already-authenticated case (page refresh, direct navigation to editor).
    // The subscription above only fires on transitions (false→true), so if the user
    // is already logged in when the module loads, we need to fetch explicitly.
    if (useAuthStore.getState().isAuthenticated) {
      syncPlatformAdminEntitlement();
      const store = useOrganizationStore.getState();
      if (store.organizations.length === 0) {
        void bootstrapOrganizationAccess();
      }
    }
  });
}
